import React from "react";
import { SoundOutlined } from "@ant-design/icons";
import { Panel } from "@/components";
import { qqService, wangwangService, weChatService } from "@/services/customerServices";
import { Divider, Spin } from "antd";
import moment from "moment";
import ProxyImage from "@/components/ProxyImage";
import contactServiceClick from "@/utils/contactService";
import WeChatServicesModal from "@/components/dialogs/WeChatServicesModal";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { useGlobalStore } from "@/store/hooks";
import ShopType from "@/enum/ShopType";
import { useRequest } from "ahooks";
import { getLatestSystemAnnouncements } from "@/services/api/system";
import { observer } from "mobx-react-lite";
import { useModalStore } from "@/hooks/Modal/hooks";
import AnnouncementModal from "../../dialog/AnnouncementModal";
import classNames from "classnames";
import SystemAnnouncementDTO from "@/dto/system/SystemAnnouncementDTO";

const ANNOUNCEMENT_CACHE_KEY = "system_announcements_cache";

async function getLatestSystemAnnouncementsWithCache(): Promise<SystemAnnouncementDTO[]> {
    const cacheStr = localStorage.getItem(ANNOUNCEMENT_CACHE_KEY);

    const today = moment().format("YYYY-MM-DD");

    if (cacheStr) {
        try {
            const cache = JSON.parse(cacheStr);

            if (cache.date === today) {
                return cache.data;
            }
        } catch (e) {}
    }

    const data = await getLatestSystemAnnouncements();

    localStorage.setItem(
        ANNOUNCEMENT_CACHE_KEY,
        JSON.stringify({
            date: today,
            data,
        }),
    );

    return data;
}

const SystemAnnouncements: React.FC = observer(function () {
    const { data = [], loading } = useRequest(getLatestSystemAnnouncementsWithCache);

    const modalStore = useModalStore();
    const globalStore = useGlobalStore();

    if (loading) {
        return null;
    }

    if (!data?.length) {
        return <span>暂无公告</span>;
    }

    const shopId = globalStore.currentUser?.shopId;

    const shop = globalStore.shopMapping.get(shopId);
    const shopType = shop?.type;

    const sliceData = data.slice(0, 3);

    return (
        <div className="text-small">
            <h5 className="text-black font-bold  mb5">最新公告</h5>
            <div className="fb flex flex-center mb5">
                <img
                    src="https://img.alicdn.com/imgextra/i4/54458674/O1CN01AmF4ot2Dwkb6vbBRD_!!54458674.png"
                    alt=""
                    style={{ width: "15px" }}
                    className="mr3 ml3"
                />
                <a
                    href="https://qw15p3s8hv.feishu.cn/wiki/ZWJdw0aQXiSi9XkpSZ9ckGG4ncg?from=from_copylink"
                    target="_blank"
                    className="fs14"
                >
                    发货易功能更新日志[持续更新]
                </a>
            </div>

            <div id="system-announcements-table">
                {sliceData.map((announcement, index) => {
                    const isHide = shopType && announcement.excludedShopTypes?.includes(shopType);

                    if (isHide) {
                        return null;
                    }

                    return (
                        <p className={classNames("pl5", index + 1 !== sliceData.length && "mb5")} key={announcement.id}>
                            <a target="_blank" href={announcement.linkUrl}>
                                <SoundOutlined className="mr5" />
                                {announcement.dateCreated ? (
                                    <span>{moment(announcement.dateCreated).format("MM-DD")}: </span>
                                ) : null}

                                <span>{announcement.title}</span>
                            </a>
                        </p>
                    );
                })}
                <AutoLoadingButton
                    type="link"
                    className="fs12 ml5"
                    onClick={() => {
                        modalStore.openModal(AnnouncementModal);
                    }}
                >
                    查看更多
                </AutoLoadingButton>
            </div>
        </div>
    );
});

const CustomerServices: React.FC = function () {
    const modalStore = useModalStore();

    const { currentUser, shopMapping } = useGlobalStore();

    const currentUserShop = shopMapping.get(currentUser?.shopId);

    const userShopType = currentUserShop?.type;

    const isPddUser = [ShopType.PINDUODUO, ShopType.PDD_FDS, ShopType.PDD_FULFILLMENT].includes(userShopType);

    return (
        <Panel title="客户服务" className="mb20">
            <div className="p15 flex">
                <div style={{ flex: 1 }} className="flex">
                    <div className="text-black text-small mr20">
                        {!isPddUser && (
                            <p className="mb10" style={{ display: "flex", alignItems: "center" }}>
                                <span className="font-bold">客服旺旺：</span>
                                <span>{wangwangService.name}</span>
                                <a
                                    target="_blank"
                                    href={wangwangService.url}
                                    onClick={(e) => {
                                        contactServiceClick(e, wangwangService.url);
                                    }}
                                >
                                    <img
                                        src={wangwangService.icon}
                                        alt="点击这里给我发消息"
                                        title="点击这里给我发消息"
                                    />
                                </a>
                            </p>
                        )}
                        {qqService ? (
                            <p className="mb10" style={{ display: "flex", alignItems: "center" }}>
                                <span className="font-bold">Q Q客服：</span>
                                <span>{qqService.name}</span>
                                <a
                                    target="_blank"
                                    href={qqService.url}
                                    onClick={(e) => {
                                        contactServiceClick(e, qqService.url);
                                    }}
                                >
                                    <ProxyImage
                                        src={qqService.icon}
                                        alt="点击这里给我发消息"
                                        title="点击这里给我发消息"
                                    />
                                </a>
                            </p>
                        ) : null}

                        {weChatService && (
                            <p className="mb10" style={{ display: "flex", alignItems: "center" }}>
                                <span className="font-bold">微信客服：</span>
                                <a
                                    target="_blank"
                                    onClick={() => modalStore.openModal(WeChatServicesModal)}
                                >
                                    <ProxyImage
                                        src={weChatService.icon}
                                        alt="点击这里给我发消息"
                                        title="点击这里给我发消息"
                                    />
                                </a>
                            </p>
                        )}

                        <p className="mb10" style={{ display: "flex", alignItems: "center" }}>
                            <span className="font-bold">工作时间：</span>
                            <span>早9:00 - 12:00 晚13:00 - 21:00</span>
                        </p>
                    </div>

                    <Divider orientation="vertical" className="h" />

                    <SystemAnnouncements />
                </div>
            </div>
        </Panel>
    );
};

export default CustomerServices;
